import { formatDateRange, tripStartOf } from "../components/trip-planner/trip-date";
import { dayCity } from "./trip-route";
import type { Trip } from "../types";

type Translate = (key: string, vars?: Record<string, unknown>) => string;

/** Longest question we prefill; the chat composer is not a document. */
const MAX_ASK = 700;

/**
 * The question "Plan with MaJourneys AI" opens the chat with (owner,
 * 2026-11-04): the trip's cities, dates and party, and what is already on
 * each day, so the AI plans this trip instead of starting from nothing. The
 * guest sees it in the box and can change it before sending.
 *
 * Pure: web and app both import it.
 */
export function tripAiAsk(
  trip: Trip,
  t: Translate,
  opts: {
    lang: string;
    cityName: (slug: string) => string;
    stopTitle: (stop: Trip["days"][number]["stops"][number]) => string | null;
  },
): string {
  const cities = [...new Set(trip.days.map((d) => dayCity(trip, d)).filter(Boolean))].map(opts.cityName);
  if (cities.length === 0 && trip.city) cities.push(opts.cityName(trip.city));
  const dates = formatDateRange(tripStartOf(trip), trip.days.length, `${opts.lang}-u-nu-latn`);
  const people = trip.people ?? 2;
  const kids = trip.childAges?.length ?? 0;
  const party = kids > 0
    ? t("trip.ai.ask_party_kids", { count: people, kids, ages: trip.childAges!.join(", ") })
    : t("trip.ai.ask_party", { count: people });
  const parts = [t("trip.ai.ask", { cities: cities.join(", "), dates, party })];

  const had = trip.days
    .map((day, i) => {
      const titles = day.stops.map(opts.stopTitle).filter((x): x is string => !!x);
      return titles.length > 0 ? `${t("trip.day_label", { number: i + 1 })}: ${titles.join(", ")}` : null;
    })
    .filter((x): x is string => x !== null);
  if (had.length > 0) {
    parts.push(t("trip.ai.ask_have", { places: had.join("; ") }));
    parts.push(t("trip.ai.ask_rest"));
  }
  const text = parts.join(" ");
  return text.length > MAX_ASK ? `${text.slice(0, MAX_ASK - 1).trimEnd()}…` : text;
}

/** The chat link that opens with that question, for this trip. */
export function tripAiHref(chatHref: string, trip: Trip, ask: string): string {
  const join = chatHref.includes("?") ? "&" : "?";
  return `${chatHref}${join}ask=${encodeURIComponent(ask)}&trip=${encodeURIComponent(trip.id)}`;
}
